import { BoolNode, SearchAtom, SearchOperator } from 'src/services/common.types';
import { WorkRightType, EducationLevel, CandidateAvailabilityStatus } from 'src/services/candidates/candidates.types';

import { FieldOption, SearchFormValues } from '../types/search.types';

/**
 * Folds the top-level keyword field (item 23, JT1-1532) into the builder DSL as a single combined
 * query: the keyword becomes a `text_query` atom AND-ed with the structured criteria. When the
 * builder's top node is already AND we merge into it (flat); otherwise we nest it under a new AND.
 * An empty keyword leaves the builder DSL unchanged.
 */
export function combineKeywordAndBuilder(keyword: string, builderDsl?: BoolNode): BoolNode {
  const trimmed = (keyword ?? '').trim();
  // ONE atom carrying the whole keyword. This used to be tokenized here, one atom per word, because
  // the backend did not tokenize and a single match_phrase required the literal contiguous string —
  // which read as "keyword search not working" for multi-word queries (JT1-1532 QA). The backend
  // tokenizes text_query itself as of JT1-1609, so splitting here now costs a redundant round of
  // AND-ed atoms and hides the user's phrase from the criteria rows.
  const keywordAtoms: SearchAtom[] = trimmed
    ? [{ field: 'text_query', operator: 'match_phrase', value: trimmed }]
    : [];

  const builder = builderDsl ?? { op: 'AND', children: [] };
  const builderChildren = builder.op === 'AND' ? builder.children : [builder];

  return { op: 'AND', children: [...keywordAtoms, ...builderChildren] };
}

/**
 * Creates a search atom for the DSL
 */
export function createSearchAtom(field: string, operator: SearchOperator, value: any, variant?: string): SearchAtom {
  return {
    field,
    operator,
    value,
    variant
  };
}

/**
 * Creates a boolean node for the DSL
 */
export function createBoolNode(op: 'AND' | 'OR' | 'NOT', children: (BoolNode | SearchAtom)[]): BoolNode {
  return {
    op,
    children,
  };
}

/**
 * Builds the search DSL from form values
 */
export function buildSearchDSL(formValues: SearchFormValues): BoolNode {
  // Always use the advanced search DSL if available
  if (formValues.search_dsl) {
    return formValues.search_dsl;
  }

  // Fallback to an empty AND node if no DSL is provided
  return createBoolNode('AND', []);
}

/**
 * Get education level options
 */
export function getEducationLevelOptions(): { value: string; label: string }[] {
  return [
    { value: EducationLevel.HIGH_SCHOOL, label: 'High School' },
    { value: EducationLevel.CERTIFICATE, label: 'Certificate' },
    { value: EducationLevel.DIPLOMA, label: 'Diploma' },
    { value: EducationLevel.UNDERGRADUATE_DEGREE, label: 'Undergraduate Degree' },
    { value: EducationLevel.POSTGRADUATE_DEGREE, label: 'Postgraduate Degree' },
  ];
}

/**
 * Get work rights options
 */
export function getWorkRightsOptions(): { value: string; label: string }[] {
  return [
    { value: WorkRightType.FULL_WORK_RIGHTS, label: 'Full Work Rights' },
    { value: WorkRightType.SPONSORSHIP_REQUIRED, label: 'Sponsorship Required' },
    { value: WorkRightType.RESTRICTED_VISA, label: 'Restricted Visa' },
  ];
}

/**
 * Get availability status options
 */
export function getAvailabilityStatusOptions(): { value: string; label: string }[] {
  return [
    { value: CandidateAvailabilityStatus.AVAILABLE_NOW, label: 'Available Now' },
    { value: CandidateAvailabilityStatus.AVAILABLE_SOON, label: 'Available Soon' },
    { value: CandidateAvailabilityStatus.INTERESTED, label: 'Interested' },
    { value: CandidateAvailabilityStatus.UNAVAILABLE, label: 'Unavailable' },
  ];
}

/**
 * Get all field options for the search form
 */
export function getFieldOptions(): FieldOption[] {
  return [
    {
      id: 'text_query',
      label: 'Text Search',
      type: 'text',
      placeholder: 'Search across all fields',
      defaultOperator: 'match_phrase',
      operators: ['match_phrase'],
    },
    {
      id: 'role_title',
      label: 'Role Title',
      type: 'text',
      placeholder: 'Search by role title',
      defaultOperator: 'match_phrase',
      operators: ['match_phrase', 'eq', 'neq'],
    },
    {
      id: 'company',
      label: 'Company',
      type: 'text',
      placeholder: 'Search by company name',
      defaultOperator: 'match_phrase',
      operators: ['match_phrase', 'eq', 'neq'],
    },
    {
      id: 'skills',
      label: 'Skills',
      type: 'multiselect',
      placeholder: 'Select skills',
      defaultOperator: 'contains_any',
      operators: ['contains_any', 'contains_all', 'in', 'nin'],
    },
    {
      id: 'location',
      label: 'Location',
      type: 'multiselect',
      placeholder: 'Select locations',
      defaultOperator: 'contains_any',
      operators: ['contains_any', 'contains_all'],
    },
    {
      id: 'availability_status',
      label: 'Availability Status',
      type: 'multiselect',
      options: getAvailabilityStatusOptions(),
      defaultOperator: 'in',
      operators: ['in', 'nin'],
    },
    {
      id: 'labels',
      label: 'Labels',
      type: 'multiselect',
      placeholder: 'Select labels',
      defaultOperator: 'in',
      operators: ['in', 'nin'],
    },
    {
      id: 'work_rights',
      label: 'Work Rights',
      type: 'multiselect',
      options: getWorkRightsOptions(),
      defaultOperator: 'in',
      operators: ['in', 'nin'],
    },
    {
      id: 'notice_period_days',
      label: 'Notice Period (Days)',
      type: 'number',
      placeholder: 'Maximum notice period in days',
      defaultOperator: 'lte',
      operators: ['gt', 'gte', 'lt', 'lte'],
    },
    {
      id: 'salary_min',
      label: 'Minimum Salary',
      type: 'number',
      placeholder: 'Minimum expected salary',
      defaultOperator: 'gte',
      operators: ['gt', 'gte', 'lt', 'lte'],
      variant: {
        label: 'Period',
        options: [
          { value: 'year', label: 'Annual' },
          { value: 'month', label: 'Monthly' },
          { value: 'week', label: 'Weekly' },
          { value: 'day', label: 'Daily' },
          { value: 'hour', label: 'Hourly' },
        ]
      }
    },
    {
      id: 'salary_max',
      label: 'Maximum Salary',
      type: 'number',
      placeholder: 'Maximum expected salary',
      defaultOperator: 'lte',
      operators: ['gt', 'gte', 'lt', 'lte'],
      variant: {
        label: 'Period',
        options: [
          { value: 'year', label: 'Annual' },
          { value: 'month', label: 'Monthly' },
          { value: 'week', label: 'Weekly' },
          { value: 'day', label: 'Daily' },
          { value: 'hour', label: 'Hourly' },
        ]
      }
    },
    {
      id: 'education_level',
      label: 'Education Level',
      type: 'select',
      options: getEducationLevelOptions(),
      // must be one of `operators` — `eq` is not, so a fresh row emitted an operator the backend
      // rejects for this field
      defaultOperator: 'in',
      operators: ['in', 'nin'],
    },
    {
      id: 'education_institution',
      label: 'Education Institution',
      type: 'text',
      placeholder: 'Search by institution name',
      defaultOperator: 'match_phrase',
      operators: ['match_phrase', 'eq', 'neq'],
    },
    {
      id: 'links_host',
      label: 'Links',
      type: 'text',
      placeholder: 'Search by URL',
      defaultOperator: 'match_phrase',
      operators: ['match_phrase'],
    },
    {
      id: 'executive_summary',
      label: 'Executive Summary',
      type: 'text',
      placeholder: 'Search in executive summary',
      defaultOperator: 'match_phrase',
      operators: ['match_phrase'],
    },
  ];
}

/**
 * Creates an example DSL for the given scenario
 * Example: 10 day notice period AND react as a skill but NOT in melbourne
 */
export function createExampleDSL(): BoolNode {
  return {
    op: 'AND',
    children: [
      // 10 day notice period
      {
        field: 'notice_period_days',
        operator: 'lte',
        value: 10,
      },
      // AND react as a skill
      {
        field: 'skills',
        operator: 'contains_any',
        value: ['React'],
      },
      // BUT NOT in melbourne
      {
        op: 'NOT',
        children: [
          {
            field: 'location',
            operator: 'contains_any',
            value: ['Melbourne'],
          },
        ],
      },
    ],
  };
}